import { API_BASE_URL } from "../config";
import { RegistrationResponse } from "../types";

const apiUrl = `${API_BASE_URL}/api`;

export class RegistrationForbiddenError extends Error {
  constructor(message = "You are not authorised to use this service.") {
    super(message);
    this.name = "RegistrationForbiddenError";
  }
}

export async function fetchRegistrationAuthorisation(
  accessToken: string
): Promise<RegistrationResponse> {
  const url = `${apiUrl}/registration/authorisation/status`;

  const options = {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      const errorJson = await response.json().catch(() => undefined);
      let errorDetail: string | undefined;

      if (errorJson && typeof errorJson === "object") {
        errorDetail = errorJson.detail || errorJson.message;
      }

      if (!errorDetail) {
        const errorText = await response.text().catch(() => "");
        errorDetail = errorText || undefined;
      }

      if (response.status === 403) {
        throw new RegistrationForbiddenError(
          errorDetail || "You are not authorised to use this service. Contact your administrator."
        );
      }

      throw new Error(
        errorDetail
          ? `Failed to fetch registration status (${response.status}): ${errorDetail}`
          : `Failed to fetch registration status. Server responded with ${response.status}.`
      );
    }

    const data = await response.json();
    const authorised = Boolean(data.authorised);
    const roles = Array.isArray(data.roles) ? data.roles : [];

    return {
      authorised,
      roles,
    };
  } catch (error) {
    const name = (error as Error)?.name;
    if (error instanceof RegistrationForbiddenError || name === "RegistrationForbiddenError") {
      throw error as Error; // Preserve backend detail message.
    }
    console.error(
      "A network error occurred during the registration authorisation API call:",
      error
    );
    throw new Error(
      "Network request failed. Please ensure the backend server is running and accessible."
    );
  }
}
